import { useState, useEffect } from 'react';
import { Button, PageContainer } from '../../../components/common';
import { ELEMENT_IDS } from '../../../constants/ids';
import { Snackbar, Alert } from '@mui/material';
import './addOnsAlternate.css';
import { PlusCircle, Check } from 'lucide-react';
import { COMPANY_CONFIG } from '../../../constants/companyConfig';
const displayCompanyName = COMPANY_CONFIG.name;

const addOns = [
  {
    id: 1,
    title: 'Whole-Home Wi-Fi 6 and ONT',
    price: '$5.00',
    description:
      'ONT (Optical Network Terminal) is a device that connects your home to high-speed fiber optic internet, offering faster and more reliable internet access for all your online activities.',
    type: 'addon',
  },
  {
    id: 2,
    title: 'Wi-Fi 6 Mesh Extender',
    price: '$6.95',
    description: `Boost your home's Wi-Fi with ${displayCompanyName} Mesh Extender! Eliminate dead zones, enjoy high-speed internet everywhere. Sign up today and get 3 months free.`,
    type: 'addon',
  },
  {
    id: 3,
    title: 'Outdoor Wi-Fi 6 Mesh Extender',
    price: '$7.50',
    description:
      'Extend your Wi-Fi to the outdoors with our waterproof outdoor Wi-Fi mesh extender, featuring Wi-Fi 6 for high speed, stable connections in any environment.',
    finePrint:
      'Note: Requires technician installation. Additional installation fees may apply.',
    type: 'addon',
  },
  {
    id: 4,
    title: 'Dedicated IP',
    price: '$8.00',
    description:
      'Our dedicated IP service provides a dynamic IP address for gamers that require hosting or playing games with other users on the internet.',
    type: 'addon',
  },
];

const AddOnsAlternate = ({
  htmlContent,
  onApplyChanges,
  setHtmlContent,
  token,
}) => {
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Save add-ons data whenever selection changes
  useEffect(() => {
    const selectedAddOnsList = addOns
      .filter((addon) => selectedAddOns[addon.id])
      .map((addon) => ({
        id: addon.id,
        title: addon.title,
        price: addon.price,
        priceValue: parseFloat(addon.price.replace('$', '')),
        description: addon.description,
        finePrint: addon.finePrint,
      }));

    const totalAmount = selectedAddOnsList.reduce(
      (sum, addon) => sum + addon.priceValue,
      0
    );

    const addOnsData = {
      selectedAddOns: selectedAddOnsList,
      totalAmount: totalAmount,
      addOnsCount: selectedAddOnsList.length,
    };

    console.log('🎯 AddOnsAlternate - selectedAddOns state:', selectedAddOns);
    console.log('🎯 AddOnsAlternate - Auto-saving data:', addOnsData);

    if (onApplyChanges) {
      onApplyChanges(addOnsData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddOns]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAddOnToggle = (id) => {
    console.log('handleAddOnToggle called with id:', id);
    const wasSelected = !!selectedAddOns[id];
    const addon = addOns.find((item) => item.id === id);

    setSelectedAddOns((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));

    if (!wasSelected) {
      console.log('Showing add snackbar');
      showSnackbar(
        `${addon?.title || 'Item'} has been added to your plan!`,
        'success'
      );
    } else {
      console.log('Showing remove snackbar');
      showSnackbar(
        `${addon?.title || 'Item'} has been removed from your plan!`,
        'error'
      );
    }
  };

  return (
    <PageContainer className="addOnsNode-Container">
      <h2 className="addOnsNode-Title">Internet Add-Ons</h2>
      <p className="addOnsNode-Subtitle">
        Enhance your plan with extra features and services designed to give you
        even more.
      </p>

      <div className="addOnsNode-List">
        {addOns.map((item) => (
          <div key={item.id} className="addOnsNode-Item">
            <div className="addOnsNode-ItemContent">
              <div className="addOnsNode-ItemLeft">
                <h3 className="addOnsNode-ItemTitle">{item.title}</h3>
                <div className="addOnsNode-ItemPrice">{item.price}</div>
              </div>
              <div className="addOnsNode-ItemDescription">
                <p>{item.description}</p>
                {item.finePrint && (
                  <p className="addOnsNode-ItemFinePrint">{item.finePrint}</p>
                )}
              </div>
            </div>
            <div className="addOnsNode-ItemAction">
              {selectedAddOns[item.id] ? (
                <div
                  className="addOnsNode-IncludedButton"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Included button clicked for item:', item.id);
                    handleAddOnToggle(item.id);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAddOnToggle(item.id);
                    }
                  }}
                >
                  <div className="addOnsNode-Checkbox">
                    <Check size={12} />
                  </div>
                  Included
                </div>
              ) : (
                <div
                  className="addOnsNode-AddButton"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Add button clicked for item:', item.id);
                    handleAddOnToggle(item.id);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAddOnToggle(item.id);
                    }
                  }}
                >
                  <PlusCircle size={16} />
                  Add
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="addOnsNode-buttonRow">
        <Button
          customId={ELEMENT_IDS.PREVIOUS_STEP_BUTTON}
          label="Back"
          variant="secondary"
        />
        <Button
          customId={ELEMENT_IDS.NEXT_STEP_BUTTON}
          label="Next"
        />
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AddOnsAlternate;
